import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, X } from "lucide-react";
import type { StoredCV } from "../hooks/useCV";
import { useProfileStore } from "../stores/profile.store";

interface SelectCVModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (cv: StoredCV) => void;
  cvs: StoredCV[];
}

const SelectCVModal: React.FC<SelectCVModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  cvs,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileStore();

  const [selectedId, setSelectedId] = useState<string | null>(
    profile?.current_cv || null
  );

  useEffect(() => {
    setSelectedId(profile?.current_cv || null);
  }, [profile?.current_cv]);

  if (!isOpen) {
    return null;
  }

  const handleSelect = () => {
    const selectedCv = cvs.find(cv => cv.id === selectedId);
    if (selectedCv) {
      onSelect(selectedCv);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {t("profile.cv.selectCvFromProfile")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
          {cvs.map(cv => {
            const isSelected = cv.id === selectedId;
            return (
              <div
                key={cv.id}
                onClick={() => setSelectedId(cv.id)}
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? "border-[#763BDE] bg-[#F3EEFF]"
                    : "border-gray-300 bg-white hover:border-gray-400"
                }`}
              >
                <FileText className="w-6 h-6 mr-4 text-gray-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800 truncate">
                    {cv.filename}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("profile.cv.last_updated")}:{" "}
                    {new Date(cv.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={handleSelect}
            disabled={!selectedId}
            className="w-full text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(to right, #5042DA, #8039DF)",
            }}
          >
            {t("profile.cv.select")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectCVModal;
